// « Exercices et protocoles » (#/banque, fiche #/banque?fiche=<id>), from the Bibliothèque. Four validated blocks: header + search
// + type and category filters (UBM SearchField, SegmentedControl, DropdownMenu), a compact list (YouTube thumbnail when the video
// is qualified, honest non-media tile otherwise), a full-screen fiche (the existing DemoVideo player with the same qualified
// sources, demo cues or protocol steps), and « Poser une question à mon coach » with the item as context. Watching never
// completes a session nor adds a prescription. Filters live in module memory, so the list comes back as it was left.
import { Button } from "@ubm/ui/components/button";
import { useEffect, useState, type CSSProperties } from "react";
import { BookOpen, Check, ChevronDown, ChevronLeft, ChevronRight, ListOrdered, MessageCircle, Video } from "lucide-react";
import { SearchField } from "@ubm/ui/components/search-field";
import { SegmentedControl } from "@ubm/ui/components/segmented-control";
import { Avatar } from "@ubm/ui/components/avatar";
import { DropdownMenuContentBase, DropdownMenuItemIndicator, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuRoot, DropdownMenuTrigger, dropdownMenuClassNames as menu, useMenuHighlight } from "@ubm/ui/components/dropdown-menu";
import { StatusBar, type Route } from "./parts";
import { DemoVideo } from "./video-player";
import { messagerie } from "./messagerie-state";
import { TEAM } from "./studio-fixtures";
import { CATALOGUE, CATEGORIES, CATEGORY_LABEL, ytThumb, type BankItem, type Category } from "./banque-fixtures";
import { ShellActions } from "./shell";

interface PageProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }
type Type = "tout" | "exercice" | "protocole";
const norm = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
const readFiche = () => window.location.hash.match(/fiche=([\w-]+)/)?.[1] ?? null;
/** Filters kept for the page session (back from a fiche returns to the same filtered list). */
const memory: { q: string; type: Type; cat: Category | null } = { q: "", type: "tout", cat: null };
/** Visible meta: type, category label, and a duration only when known. The type and the category never repeat each other. */
const meta = (it: BankItem) => [it.kind === "exercice" ? "Exercice" : "Protocole", CATEGORY_LABEL[it.category], it.kind === "protocole" ? it.duration : undefined].filter(Boolean).join(" · ");
/** Searchable and shown tags (zones then objectives), without repeating the category label. */
const tags = (it: BankItem) => [...it.zones, ...it.goals].filter((t, i, a) => a.indexOf(t) === i && norm(t) !== norm(CATEGORY_LABEL[it.category]));

export function Banque({ go }: PageProps) {
  const [fiche, setFiche] = useState(readFiche);
  useEffect(() => { const on = () => setFiche(readFiche()); window.addEventListener("hashchange", on); return () => window.removeEventListener("hashchange", on); }, []);
  const item = fiche ? CATALOGUE.find(x => x.id === fiche) : undefined;
  return item ? <Fiche key={item.id} item={item} go={go} /> : <List go={go} />;
}

function List({ go }: { go: (r: Route) => void }) {
  const [q, setQ] = useState(memory.q);
  const [type, setType] = useState<Type>(memory.type);
  const [cat, setCat] = useState<Category | null>(memory.cat);
  useEffect(() => { memory.q = q; memory.type = type; memory.cat = cat; }, [q, type, cat]);
  const highlight = useMenuHighlight();
  const nq = norm(q.trim());
  const items = CATALOGUE.filter(it => (type === "tout" || it.kind === type) && (!cat || it.category === cat)
    && (!nq || [it.title, CATEGORY_LABEL[it.category], ...it.zones, ...it.goals].some(t => norm(t).includes(nq))));
  const clear = () => { setQ(""); setType("tout"); setCat(null); };

  return <div className="cc-screen cc-account cc-bank">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-account-head">
        <button type="button" className="cc-icon-btn" aria-label="Retour à la Bibliothèque" onClick={() => go("programme")}><ChevronLeft size={18} strokeWidth={2} /></button>
        <h1 className="cc-title">Exercices et protocoles</h1>
        <ShellActions />
      </header>

      <div className="cc-bank-filters">
        <SearchField className="cc-bank-search-input" label="Rechercher" placeholder="Rechercher un mouvement, une zone ou un objectif" value={q} onValueChange={setQ} />
        <SegmentedControl size="sm" label="Type de contenu" value={type} onValueChange={v => setType(v as Type)}
          options={[{ value: "tout", label: "Tout" }, { value: "exercice", label: "Exercices" }, { value: "protocole", label: "Protocoles" }]} />
        <DropdownMenuRoot onOpenChange={open => { if (open) highlight.reset(); }}>
          <DropdownMenuTrigger asChild><button type="button" className="cc-bank-cat" data-active={cat ? true : undefined}>{cat ? CATEGORY_LABEL[cat] : "Toutes les catégories"} <ChevronDown size={14} strokeWidth={2} aria-hidden="true" /></button></DropdownMenuTrigger>
          <DropdownMenuPortal><DropdownMenuContentBase className={`${menu.menu} cc-bank-menu`} side="bottom" align="start" sideOffset={6} collisionPadding={12} loop aria-label="Catégorie" {...highlight.panelProps}>
            {highlight.element}
            <DropdownMenuRadioGroup value={cat ?? ""} onValueChange={v => setCat((v || null) as Category | null)}>
              {["", ...CATEGORIES].map((c, i) => <DropdownMenuRadioItem key={c || "all"} value={c} className={menu.item} style={{ "--i": i } as CSSProperties}>
                <span className={menu.icon} aria-hidden="true"><DropdownMenuItemIndicator><Check size={15} strokeWidth={2} /></DropdownMenuItemIndicator></span>{c ? CATEGORY_LABEL[c as Category] : "Toutes les catégories"}
              </DropdownMenuRadioItem>)}
            </DropdownMenuRadioGroup>
          </DropdownMenuContentBase></DropdownMenuPortal>
        </DropdownMenuRoot>
      </div>

      {items.length === 0 ? <div className="cc-card cc-bank-empty"><p>Aucun contenu trouvé</p><Button variant="ghost" size="sm" onClick={clear}>Effacer les filtres</Button></div>
        : <ul className="cc-card cc-bank-list">{items.map(it => {
          const thumb = it.kind === "exercice" ? ytThumb(it.video) : undefined;
          return <li key={it.id}><button type="button" className="cc-bank-row" onClick={() => { window.location.hash = `#/banque?fiche=${it.id}`; }}>
            {thumb ? <span className="cc-bank-thumb"><img src={thumb} alt="" loading="lazy" /><Video size={12} strokeWidth={2.4} aria-hidden="true" /></span>
              : <span className="cc-bank-tile" aria-hidden="true">{it.kind === "protocole" ? <ListOrdered size={18} strokeWidth={1.8} /> : <BookOpen size={18} strokeWidth={1.8} />}</span>}
            <span className="cc-bank-row-text"><b>{it.title}</b><small>{meta(it)}</small></span>
            <ChevronRight size={16} color="#c4c4c9" aria-hidden="true" />
          </button></li>;
        })}</ul>}
      <p className="cc-home-demo cc-account-demo">Prototype : catalogue de démonstration (exercices de ta séance, protocoles de la Bibliothèque).</p>
    </div>
  </div>;
}

function Fiche({ item, go }: { item: BankItem; go: (r: Route) => void }) {
  const coach = TEAM[0];
  return <div className="cc-screen cc-account cc-bank cc-bank-fiche">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-account-head">
        <button type="button" className="cc-icon-btn" aria-label={/retour=onboarding/.test(window.location.hash) ? "Retour à la configuration" : "Retour aux exercices et protocoles"} onClick={() => { window.location.hash = /retour=onboarding/.test(window.location.hash) ? "#/onboarding" : "#/banque"; }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <h1 className="cc-title">{item.title}</h1>
        <ShellActions />
      </header>
      <p className="cc-bank-meta">{meta(item)}</p>
      {item.description && <p className="cc-home-p">{item.description}</p>}
      {tags(item).length > 0 && <p className="cc-bank-meta">{tags(item).join(" · ")}</p>}

      {item.kind === "exercice" && item.video ? <div className="cc-bank-video"><DemoVideo video={item.video} label={`Démonstration : ${item.title}`} /></div>
        : <div className="cc-seance-video" role="note"><Video size={18} strokeWidth={1.8} /><span><b>Vidéo de démonstration à venir</b></span></div>}

      {item.kind === "exercice" ? <section className="cc-bank-block">
        <h2 className="cc-account-group-title">Consignes</h2>
        <div className="cc-card cc-bank-text">
          {item.cues.length ? <ol className="cc-home-ex">{item.cues.map(c => <li key={c}>{c}</li>)}</ol> : <p className="cc-bp-note">Consignes à venir.</p>}
        </div>
        <p className="cc-bp-note">Consignes d'exécution de démonstration, pas une prescription d'entraînement.</p>
      </section> : <section className="cc-bank-block">
        <h2 className="cc-account-group-title">Déroulé{item.duration ? ` · ${item.duration}` : ""}</h2>
        <div className="cc-card cc-bank-text"><ol className="cc-home-ex">{item.steps.map(s => <li key={s}>{s}</li>)}</ol></div>
        <p className="cc-bp-note">Instructions de démonstration ({item.source}), pas une prescription d'entraînement ni médicale.</p>
      </section>}

      <section className="cc-bank-block">
        <button type="button" className="cc-card cc-bank-ask" onClick={() => { messagerie.startWithContext("alexandre", { label: `À propos de ${item.title} · ${item.kind === "exercice" ? "exercice" : "protocole"}`, href: `#/banque?fiche=${item.id}` }); window.location.hash = "#/messages?fil=alexandre"; }}>
          {coach ? <Avatar name={coach.firstName} src={coach.photo} size="sm" /> : <MessageCircle size={18} />}
          <span>Poser une question à mon coach</span>
          <ChevronRight size={16} color="#c4c4c9" aria-hidden="true" />
        </button>
      </section>
    </div>
  </div>;
}
